/** What a tab is called (I-3641): the user's name, else what lies in it. */
import type { useI18n } from "vue-i18n";
import { appForPath } from "@/integration";
import { findTile, listTiles, readStoredTree, type TileLeaf } from "./tileTree";
import type { StoredTiles, TileWorkspace } from "./useTileWorkspaces";

type Translate = ReturnType<typeof useI18n>["t"];

/** A new tab starts on the start page, like Connect itself. */
export const HOME_ROUTE = "/";

/** The shell's own pages: no app behind them, but a name all the same. */
const SHELL_PAGES: Readonly<Record<string, string>> = {
  "/settings": "connect.command.settings",
  "/notifications": "connect.command.notifications",
  "/mentions": "connect.command.mentions",
  "/people": "connect.command.people",
  "/changelog": "connect.command.changelog",
  "/downloads": "connect.command.downloads",
  "/formica_id": "connect.command.formicaId",
};

/** What lies at a route: its app or the shell's page; empty when it is neither. */
export function routeLabel(route: string, t: Translate): string {
  const app = appForPath(route);
  if (app) return t(app.labelKey);
  const path = route.split(/[?#]/)[0];
  if (path === HOME_ROUTE) return t("connect.tiles.home");
  const page = Object.keys(SHELL_PAGES).find(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`),
  );
  return page ? t(SHELL_PAGES[page]) : "";
}

/** What a running or stored tile says it shows; empty when it says nothing. */
export function tileCaption(tile: TileLeaf): string {
  return tile.title || tile.area || "";
}

/** „AN-2026-00100 + Calc“; empty for a tab that only shows the start page. */
function contentLabel(workspace: TileWorkspace, t: Translate): string {
  return listTiles(workspace.root)
    .filter((tile) => tile.route && tile.route !== HOME_ROUTE)
    .map((tile) => tileCaption(tile) || routeLabel(tile.route!, t))
    .filter(Boolean)
    .join(" + ");
}

export function tabLabel(workspace: TileWorkspace, t: Translate): string {
  if (workspace.label) return workspace.label;
  const content = contentLabel(workspace, t);
  if (content) return content;
  const home = listTiles(workspace.root).some(
    (tile) => tile.route === HOME_ROUTE,
  );
  return home ? t("connect.tiles.home") : t("connect.tiles.newTab");
}

/** Where a page or tile stands, most telling first. */
export interface PageLocation {
  /** The open document or record. */
  title?: string;
  /** The area of the app around it. */
  area?: string;
  /** The app or the shell's page, when nothing narrower says where it is. */
  app?: string;
}

/**
 * „AN-2026-00100 · Angebote“, „Angebote“, „Brief · Write“, „Mail“. `withArea` only
 * concerns what stands beside a name: a page without one still says where it is.
 */
export function locationName(
  { title, area, app }: PageLocation,
  withArea = true,
): string {
  if (title && !withArea) return title;
  return [title, area || app].filter(Boolean).join(" · ");
}

/**
 * What the browser tab says of a tab of the bar (I-3777): the tile the user is in,
 * however many it holds. The name of the whole tab only stands in for an empty tile.
 */
export function activeTileName(
  workspace: TileWorkspace | null | undefined,
  t: Translate,
  withArea = true,
): string {
  if (!workspace) return "";
  const tile = findTile(workspace.root, workspace.activeTileId);
  const name =
    tile?.route &&
    locationName(
      {
        title: tile.title,
        area: tile.area,
        app: routeLabel(tile.route, t),
      },
      withArea,
    );
  return name || tabLabel(workspace, t);
}

/** A stored tab by its id; empty when it is gone or only shows the start page. */
export function storedTabLabel(
  stored: StoredTiles | null | undefined,
  workspaceId: string,
  t: Translate,
): string {
  const raw = stored?.workspaces?.[workspaceId]?.[0];
  const root = readStoredTree(raw?.root);
  if (!raw || !root) return "";
  const workspace = { ...raw, root };
  return workspace.label || contentLabel(workspace, t);
}
